import { Logo } from "@/components/branding/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { authService } from "@/services/auth.service/auth.api";

import {
  ArrowLeft,
  EyeIcon,
  EyeOffIcon,
  KeyRound,
  Lock,
  Mail,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function ForgotPassword() {
  const [step, setStep] = useState<"email" | "reset">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      toast({
        title: "Erro",
        description: "Informe seu e-mail",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    const [res, err] = await authService.requestPasswordReset(email.trim());
    setIsLoading(false);
    if (err) {
      toast({
        title: "Erro",
        description: err.msg || "Não foi possível enviar o e-mail",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Verifique seu e-mail", description: res?.msg });
    setStep("reset");
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) {
      toast({
        title: "Erro",
        description: "Informe o código recebido por e-mail",
        variant: "destructive",
      });
      return;
    }
    if (newPassword.length < 6) {
      toast({
        title: "Erro",
        description: "A nova senha precisa ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    const [res, err] = await authService.resetPassword(
      email.trim(),
      code.trim(),
      newPassword
    );
    setIsLoading(false);
    if (err) {
      toast({
        title: "Erro",
        description: err.msg || "Não foi possível redefinir a senha",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Sucesso",
      description: res?.msg || "Senha alterada com sucesso!",
    });
    navigate("/login");
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-card shadow-lg rounded-lg p-8 w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Logo size={32} textClassName="text-2xl" />
          </div>
          <h1 className="text-2xl font-bold">
            {step === "email" ? "Recuperar senha" : "Digite o código"}
          </h1>
          <p className="text-muted-foreground">
            {step === "email"
              ? "Informe seu e-mail para receber um código de recuperação"
              : `Enviamos um código para ${email}`}
          </p>
        </div>

        {step === "email" ? (
          <form onSubmit={handleRequestCode}>
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" loading={isLoading}>
                Enviar código
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="code" className="block text-sm font-medium">
                  Código recebido por e-mail
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="code"
                    type="text"
                    placeholder="Ex: A1B2"
                    className="pl-10 uppercase"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium"
                >
                  Nova senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-8 w-8 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium"
                >
                  Confirmar nova senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" loading={isLoading}>
                Redefinir senha
              </Button>

              <button
                type="button"
                className="w-full text-center text-sm text-primary hover:underline"
                onClick={() => setStep("email")}
              >
                Não recebeu? Enviar código novamente
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center text-sm">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Voltar para o login
          </Link>
        </div>
      </div>
    </div>
  );
}
